/**
 * GoalsPage
 * Goals dashboard with filtering and list/grid views
 * BP-06-03: Care Team & Goals Frontend
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, FileText, Target, Loader2 } from 'lucide-react';
import { useGoals, useGoalsSummary } from '@/features/goals/hooks/useGoals';
import { useCareTeamGoalsStore } from '@/features/care-team/stores/careTeamStore';
import { GoalCard, GoalFilters } from '../components';
import type { Goal, GoalFilters as GoalFiltersType } from '@/features/goals/types/goalsTypes';

interface GoalsPageProps {
  childId?: string;
}

export function GoalsPage({ childId: propChildId }: GoalsPageProps) {
  const { childId: paramChildId } = useParams<{ childId: string }>();
  const childId = propChildId || paramChildId!;
  const navigate = useNavigate();

  const { goalFilters, setGoalFilters, clearGoalFilters } = useCareTeamGoalsStore();
  const [activeTab, setActiveTab] = useState<'all' | 'iep'>('all');

  const {
    data: goalsData,
    isLoading,
    error,
  } = useGoals(childId, activeTab === 'iep' ? { ...goalFilters, isIepGoal: true } : goalFilters);

  const { data: summary, isLoading: isSummaryLoading } = useGoalsSummary(childId);

  const handleGoalClick = (goal: Goal) => {
    navigate(`/children/${childId}/goals/${goal.id}`);
  };

  const handleFilterChange = (filters: GoalFiltersType) => {
    setGoalFilters(filters);
  };

  const goals = goalsData?.items || [];

  // Summary stats
  const stats = summary || {
    total: goals.length,
    byStatus: {
      not_started: 0,
      in_progress: 0,
      achieved: 0,
      discontinued: 0,
    },
    avgProgress: 0,
    iepGoals: 0,
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Error loading goals. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold text-navy">Goals</h1>
          <p className="text-warm-gray mt-1">Track progress toward developmental milestones</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/children/${childId}/goals/iep-report`)}
            className="inline-flex items-center gap-2 px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
          >
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">IEP Report</span>
          </button>
          <button
            onClick={() => navigate(`/children/${childId}/goals/new`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white hover:bg-sage-dark rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New Goal</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
          <div className="text-sm text-warm-gray mb-1">Total Goals</div>
          <div className="text-2xl font-display font-semibold text-navy">
            {isSummaryLoading ? '-' : stats.total}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
          <div className="text-sm text-warm-gray mb-1">In Progress</div>
          <div className="text-2xl font-display font-semibold text-sage">
            {isSummaryLoading ? '-' : stats.byStatus.in_progress}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
          <div className="text-sm text-warm-gray mb-1">Achieved</div>
          <div className="text-2xl font-display font-semibold text-green-600">
            {isSummaryLoading ? '-' : stats.byStatus.achieved}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
          <div className="text-sm text-warm-gray mb-1">Avg Progress</div>
          <div className="text-2xl font-display font-semibold text-navy">
            {isSummaryLoading ? '-' : `${stats.avgProgress}%`}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-cream rounded-lg inline-flex mb-6">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'all' ? 'bg-white text-navy shadow-soft' : 'text-warm-gray hover:text-navy'
          }`}
        >
          <Target className="w-4 h-4 inline mr-2" />
          All Goals
        </button>
        <button
          onClick={() => setActiveTab('iep')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'iep' ? 'bg-white text-navy shadow-soft' : 'text-warm-gray hover:text-navy'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          IEP Goals ({stats.iepGoals})
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-4 mb-6">
        <GoalFilters
          filters={goalFilters}
          onChange={handleFilterChange}
          onClear={clearGoalFilters}
          showIepFilter={activeTab !== 'iep'}
        />
      </div>

      {/* Goals List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-sage" />
        </div>
      ) : goals.length === 0 ? (
        <div className="text-center py-12 bg-cream/50 rounded-xl">
          <Target className="w-12 h-12 mx-auto mb-3 text-warm-gray opacity-50" />
          <h3 className="font-medium text-navy mb-1">No Goals Found</h3>
          <p className="text-sm text-warm-gray mb-4">
            {Object.keys(goalFilters).length > 0
              ? 'Try adjusting your filters'
              : 'Start tracking developmental progress by creating your first goal'}
          </p>
          <button
            onClick={() => navigate(`/children/${childId}/goals/new`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white hover:bg-sage-dark rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create First Goal
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {goals.map((goal) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              onClick={() => handleGoalClick(goal)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {goalsData && goalsData.total > goalsData.items.length && (
        <div className="mt-8 flex justify-center">
          <p className="text-sm text-warm-gray">
            Showing {goals.length} of {goalsData.total} goals
          </p>
        </div>
      )}
    </div>
  );
}

export default GoalsPage;


